import {
  Grid,
  Box,
  FormControlLabel,
  Switch,
  Button,
  Typography,
} from "@mui/material";
import useValidation from "../../../hooks/validation/useValidation";
import moment from "moment";
import { textPrimary } from "../../../assets/styles/appStyles";
import useUI from "../../../hooks/context/useUI";
import useFetchEntityRecordTypes from "../../../hooks/Locations/useFetchEntityRecordTypes";
import { AssociationDetailsViewModel } from "../../../Models/Association/association-details-viewmodels";
import RecordType from "../../../Models/Locations/recordType";
import AppDatePicker from "../../AddLocation/Components/AppDatePicker";
import GenericSelectInput from "../../AddLocation/Components/UI/GenericSelectInput";

export default function AssociationForm({
  recordTypes,
  providers,
  associationIndex,
  formKeyName,
  form,
  associationData,
  setAssociationData,
}: {
  recordTypes: RecordType[];
  providers: any[];
  associationIndex: number;
  formKeyName: string;
  form: any;
  associationData?: AssociationDetailsViewModel;
  setAssociationData?: (data: any) => void;
}) {
  const { isAssociationRecordTypesValid } = useValidation();
  const { showSnackbar } = useUI();

  const handleChange = (recordIndex: number, field: string, value: any) => {
    const path = `${formKeyName}.${associationIndex}.associationRecordTypes.${recordIndex}.${field}`;
    form.setFieldValue(path, value);
    form.setFieldTouched(path, true, true);

    if (setAssociationData) {
      setAssociationData((prev: any) => {
        const updated = { ...prev };
        const associations = [...updated.entityAssociations];
        const targetAssoc = associations[associationIndex];
        if (targetAssoc) {
          const updatedRecords = [...targetAssoc.associationRecordTypes];
          updatedRecords[recordIndex] = {
            ...updatedRecords[recordIndex],
            [field]: value,
          };
          targetAssoc.associationRecordTypes = updatedRecords;
          associations[associationIndex] = targetAssoc;
        }
        updated.entityAssociations = associations;
        return updated;
      });
    }
  };

  const onAddRecordType = async () => {
    const oldRecordTypes =
      form.values[formKeyName][associationIndex]?.associationRecordTypes || [];

    const valid = isAssociationRecordTypesValid(oldRecordTypes);
    if (!valid) {
      oldRecordTypes.forEach((_: any, idx: number) => {
        form.setFieldTouched(
          `${formKeyName}.${associationIndex}.associationRecordTypes.${idx}.recordTypeId`,
          true
        );
      });
      showSnackbar(["Please fix the validation errors first"], "error");
      return;
    }

    const newRecord = {
      id: 0,
      recordTypeId: null,
      custodianProviderId: null,
      dateFrom: null,
      dateTo: null,
      disabled: false,
    };

    const newRecords = [...oldRecordTypes, newRecord];
    form.setFieldValue(
      `${formKeyName}.${associationIndex}.associationRecordTypes`,
      newRecords
    );

    if (setAssociationData) {
      setAssociationData((prev: any) => {
        const updated = { ...prev };
        updated.entityAssociations[associationIndex].associationRecordTypes =
          newRecords;
        return updated;
      });
    }

    await form.validateForm();
  };

  const recordTypesArray =
    form.values[formKeyName][associationIndex]?.associationRecordTypes || [];

  const childEntityId =
    associationData?.entityAssociations?.[associationIndex]?.childEntityId ??
    form.values[formKeyName][associationIndex]?.childEntityId;

  const { recordTypesList } = useFetchEntityRecordTypes(childEntityId);
  const mergedRecordTypes =
    recordTypesList?.length > 0 ? recordTypesList : recordTypes;

  const disabledEntityStatus = associationData?.entityAssociations?.[associationIndex]?.disabled;

  return (
    <Grid item xs={10}>
      {recordTypesArray?.map((item: any, index: number) => {
        const validRecordTypeValue = mergedRecordTypes.some(
          (rt) => rt.id === item.recordTypeId
        )
          ? item.recordTypeId
          : "";

        const validProviderValue = providers.some(
          (p) => p.id === item.custodianProviderId
        )
          ? item.custodianProviderId
          : null;

        const recordTypeTouched =
          form.touched[formKeyName]?.[associationIndex]
            ?.associationRecordTypes?.[index]?.recordTypeId;

        const recordTypeError =
          form.errors[formKeyName]?.[associationIndex]
            ?.associationRecordTypes?.[index]?.recordTypeId;

        const hasRecordTypeError = !!(recordTypeTouched && recordTypeError);

        // Filter out the already selected recordTypes from mergedRecordTypes
        const availableRecordTypes = mergedRecordTypes.filter(
          (rt) =>
            !recordTypesArray.some(
              (selectedItem: any) => selectedItem.recordTypeId === rt.id
            ) || item.recordTypeId === rt.id
        );

        return (
          <Box key={item.id} mb={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <GenericSelectInput
                  label="Record Type"
                  value={validRecordTypeValue}
                  options={availableRecordTypes}
                  onChange={(value: any) => {
                    handleChange(index, "recordTypeId", Number(value));
                    if (value) {
                      form.setFieldError(
                        `${formKeyName}.${associationIndex}.associationRecordTypes.${index}.recordTypeId`,
                        undefined
                      );
                    }
                  }}
                  onBlur={() =>
                    form.setFieldTouched(
                      `${formKeyName}.${associationIndex}.associationRecordTypes.${index}.recordTypeId`,
                      true
                    )
                  }
                  errorMsg={hasRecordTypeError ? recordTypeError : ""}
                  hasError={hasRecordTypeError}
                  required
                  name=""
                  disabled={disabledEntityStatus}
                />
              </Grid>

              <Grid item xs={2}>
                <GenericSelectInput
                  label="Provider"
                  value={validProviderValue}
                  options={[{ id: null, name: "None" }, ...providers]}
                  onChange={(value: any) =>
                    handleChange(index, "custodianProviderId", value)
                  }
                  onBlur={() =>
                    form.setFieldTouched(
                      `${formKeyName}.${associationIndex}.associationRecordTypes.${index}.custodianProviderId`,
                      true
                    )
                  }
                  name=""
                  hasError={false}
                  errorMsg=""
                  disabled={disabledEntityStatus}
                />
              </Grid>

              <Grid item xs={4}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <AppDatePicker
                      label="From"
                      value={item.dateFrom}
                      onChange={(value: any) =>                        
                        handleChange(
                          index,
                          "dateFrom",
                          moment(value).format("YYYY-MM-DD")
                        )
                      }
                      keyName=""
                      errorMsg=""
                      disabled={disabledEntityStatus}
                      maxDate={item.dateTo ? moment(item.dateTo).format("YYYY-MM-DD") : undefined}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <AppDatePicker
                      label="To"
                      value={item.dateTo}
                      onChange={(value: any) =>
                        handleChange(
                          index,
                          "dateTo",
                          moment(value).format("YYYY-MM-DD")
                        )
                      }
                      keyName=""
                      errorMsg=""
                      disabled={disabledEntityStatus}
                      minDate={item.dateFrom ? moment(item.dateFrom).format("YYYY-MM-DD") : undefined}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={2}>
                {item.id && item.id > 0 && recordTypesArray.length >= 1 ? (
                  <FormControlLabel
                    control={
                      <Switch
                        disabled={disabledEntityStatus}
                        checked={!item.disabled}
                        onChange={(e) =>
                          handleChange(index, "disabled", !e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={item.disabled ? "Disabled" : "Enabled"}
                  />
                ) : (
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={() => {
                      const updatedRecords = [...recordTypesArray];
                      updatedRecords.splice(index, 1);
                      form.setFieldValue(
                        `${formKeyName}.${associationIndex}.associationRecordTypes`,
                        updatedRecords
                      );
                      if (setAssociationData) {
                        setAssociationData((prev: any) => {
                          const updated = { ...prev };
                          updated.entityAssociations[
                            associationIndex
                          ].associationRecordTypes = updatedRecords;
                          return updated;
                        });
                      }
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        );
      })}

      {recordTypesList?.length !== recordTypesArray?.length && !disabledEntityStatus &&
        <Grid container spacing={2} mt={1}>
          <Grid item xs={3}>
            <Button variant="text" onClick={onAddRecordType}>
              Add Another Record Type
            </Button>
          </Grid>
        </Grid>}

      <Grid container justifyContent="flex-end" mt={2}>
        <Grid item xs={2}>
          <Typography sx={textPrimary}>
            Total Records: {recordTypesArray.length}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
